import { NoteWarning, Evidence, Ban, Dossier, AssociatedItem, TicketLink, TicketItem } from '../types/player';
import { TicketTranscriptResponse } from '../types/ticketTranscript';

const NOTES_API_BASE_URL = 'https://spy.playcdu.co/api/notes';

/** Error thrown for non-2xx transcript responses; carries the status for per-code UX. */
export class NotesApiError extends Error {
  status: number;
  body: string;
  /** Seconds parsed from the Retry-After header (429), when the backend exposes it via CORS. */
  retryAfterSeconds?: number;
  /** Upstream transcript URL from a 413 { error, transcript_url } body. */
  transcriptUrl?: string;

  constructor(status: number, statusText: string, body: string, retryAfter?: string | null) {
    let message = `API Error: ${status} ${statusText}`;
    let transcriptUrl: string | undefined;
    try {
      const parsed = JSON.parse(body) as { error?: string; transcript_url?: string };
      if (parsed && typeof parsed.error === 'string') message = parsed.error;
      else message += ` - ${body}`;
      if (parsed && typeof parsed.transcript_url === 'string') transcriptUrl = parsed.transcript_url;
    } catch {
      if (body) message += ` - ${body}`;
    }
    super(message);
    this.name = 'NotesApiError';
    this.status = status;
    this.body = body;
    this.transcriptUrl = transcriptUrl;
    const retrySeconds = retryAfter ? Number(retryAfter) : NaN;
    if (Number.isFinite(retrySeconds) && retrySeconds > 0) {
      this.retryAfterSeconds = Math.ceil(retrySeconds);
    }
  }
}

interface PaginatedNotesResponse {
  page: number;
  limit: number;
  results: NoteWarning[];
}

interface PaginatedBansResponse {
  page: number;
  limit: number;
  results: Ban[];
}

class NotesApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${NOTES_API_BASE_URL}${endpoint}`, {
      ...options,
      // Cloudflare Access handles authentication via cookies, 
      // so no explicit auth headers are needed here.
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }
    return response.json();
  }

  async addTextEvidence(itemId: string, itemType: 'note' | 'warning' | 'ban' | 'calendar_event' | 'incident', content: string, confidential?: boolean): Promise<Evidence> {
    return this.request<Evidence>('/evidence/add-text', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemId, itemType, content, ...(confidential ? { confidential: true } : {}) }),
    });
  }

  async getWarningsForPlayer(uuid: string): Promise<NoteWarning[]> {
    return this.request<NoteWarning[]>(`/warnings?uuid=${uuid}`);
  }

  async getNotesForPlayer(uuid: string): Promise<NoteWarning[]> {
    return this.request<NoteWarning[]>(`/notes?uuid=${uuid}`);
  }

  async getAllEntries(page: number = 1, limit: number = 50, type: 'note' | 'warning' | 'all' = 'all'): Promise<PaginatedNotesResponse> {
    return this.request<PaginatedNotesResponse>(`/all?type=${type}&page=${page}&limit=${limit}`);
  }
  
  async getEntriesByCreator(email: string): Promise<NoteWarning[]> {
    return this.request<NoteWarning[]>(`/created-by/${encodeURIComponent(email)}`);
  }

  async createEntry(data: {
    uuid: string;
    discord_id?: string;
    type: 'note' | 'warning';
    content: string;
    metadata?: string;
  }): Promise<{ ok: boolean; id: string }> {
    return this.request<{ ok: boolean; id: string }>('/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteEntry(id: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request<{ ok: boolean; deleted: string }>(`/delete/${id}`, {
      method: 'DELETE',
    });
  }

  async getAuthenticatedUser(): Promise<{ email: string; groups: string[] }> {
    return this.request<{ email: string; groups: string[] }>('/me');
  }

  // Evidence Management
  async listEvidenceForItem(itemId: string): Promise<Evidence[]> {
    return this.request<Evidence[]>(`/item/${itemId}/evidence`);
  }

  async getDossierForItem(itemId: string): Promise<Dossier> {
    return this.request<Dossier>(`/item/${itemId}/dossier`);
  }

  async getRecentItems(): Promise<AssociatedItem[]> {
    return this.request<AssociatedItem[]>(`/items/recent`);
  }

  async uploadEvidence(file: File, itemId: string, itemType: 'note' | 'warning' | 'ban' | 'calendar_event' | 'incident', confidential?: boolean): Promise<Evidence> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('itemId', itemId);
    formData.append('itemType', itemType);
    if (confidential) formData.append('confidential', 'true');

    const response = await fetch(`${NOTES_API_BASE_URL}/evidence/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }
    return response.json();
  }

  async uploadMultipleEvidence(files: File[], itemId: string, itemType: 'note' | 'warning' | 'ban' | 'calendar_event' | 'incident', confidential?: boolean): Promise<{
    success: boolean;
    uploaded: Evidence[];
    failed: { filename: string; error: string; }[];
    totalFiles: number;
    successCount: number;
    failedCount: number;
  }> {
    const formData = new FormData();
    
    // Add all files to the form data
    files.forEach(file => {
      formData.append('files', file);
    });
    
    formData.append('itemId', itemId);
    formData.append('itemType', itemType);
    if (confidential) formData.append('confidential', 'true');

    const response = await fetch(`${NOTES_API_BASE_URL}/evidence/upload-multiple`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }
    return response.json();
  }

  async deleteEvidence(id: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/evidence/${id}`, {
      method: 'DELETE',
    });
  }

  async patchEvidence(id: string, data: { confidential?: boolean }): Promise<Evidence> {
    return this.request<Evidence>(`/evidence/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async searchEvidenceByFilename(filename: string): Promise<Evidence[]> {
    return this.request<Evidence[]>(`/evidence/search?filename=${encodeURIComponent(filename)}`);
  }

  async getEvidenceById(id: string): Promise<Evidence> {
    return this.request<Evidence>(`/evidence/${encodeURIComponent(id)}`);
  }

  async getEvidenceByUploader(email: string): Promise<Evidence[]> {
    return this.request<Evidence[]>(`/evidence/uploader/${encodeURIComponent(email)}`);
  }

  async getAllEvidence(): Promise<Evidence[]> {
    return this.request<Evidence[]>('/evidence/all');
  }

  async linkEvidence(evidenceId: string, itemId: string, itemType: 'note' | 'warning' | 'ban'): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>('/evidence/link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ evidenceId, itemId, itemType }),
    });
  }

  async linkItems(sourceItemId: string, targetItemId: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>('/item/link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceItemId, targetItemId }),
    });
  }

  async unlinkItem(sourceItemId: string, targetItemId: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>('/item/unlink', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceItemId, targetItemId }),
    });
  }

  // Ban Management
  async createBan(data: Omit<Ban, 'ban_uuid' | 'banner_email' | 'created' | 'enforced'>): Promise<Ban> {
    return this.request<Ban>('/bans', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getAllBans(page: number = 1, limit: number = 50, scope?: string, scope_target?: string): Promise<PaginatedBansResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (scope) params.append('scope', scope);
    if (scope_target) params.append('scope_target', scope_target);
    return this.request<PaginatedBansResponse>(`/bans?${params.toString()}`);
  }

  async getBansByBanner(email: string): Promise<Ban[]> {
    return this.request<Ban[]>(`/bans/banner/${encodeURIComponent(email)}`);
  }

  async getBansForUser(user_uuid: string): Promise<Ban[]> {
    return this.request<Ban[]>(`/bans/user/${user_uuid}`);
  }

  async updateBan(ban_uuid: string, data: Partial<Pick<Ban, 'reason' | 'expires' | 'enforced'>>): Promise<Ban> {
    return this.request<Ban>(`/bans/${ban_uuid}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteBan(ban_uuid: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/bans/${ban_uuid}`, {
      method: 'DELETE',
    });
  }

  // Ticket Linking
  async linkTicket(ticketId: number, itemId: string, itemType: 'note' | 'warning' | 'ban' | 'incident'): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>('/tickets/link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket_id: ticketId, item_id: itemId, item_type: itemType }),
    });
  }

  async unlinkTicket(ticketId: number, itemId: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/tickets/unlink?ticket_id=${ticketId}&item_id=${itemId}`, {
      method: 'DELETE',
    });
  }

  async getTicketsForItem(itemId: string): Promise<TicketLink[]> {
    return this.request<TicketLink[]>(`/item/${itemId}/tickets`);
  }

  async getItemsForTicket(ticketId: number): Promise<TicketItem[]> {
    return this.request<TicketItem[]>(`/ticket/${ticketId}/items`);
  }

  /**
   * Fetch a closed ticket's HTML transcript (JSON envelope: metadata + html).
   * Admin-and-up only; throws NotesApiError carrying the status so the UI can
   * distinguish 403/404/413/429/502/503.
   */
  async getTicketTranscript(ticketId: number): Promise<TicketTranscriptResponse> {
    const response = await fetch(
      `${NOTES_API_BASE_URL}/ticket/${encodeURIComponent(String(ticketId))}/transcript/html`,
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new NotesApiError(
        response.status,
        response.statusText,
        errorText,
        response.headers.get('Retry-After'),
      );
    }
    return response.json();
  }
}

export const notesApiService = new NotesApiService();
